import { resolveAssetOpeningTimesForDisplay } from '../api/assetPayloadNormalize';
import { eachCalendarDayInRange, isoToCalendarDayKey, parseCalendarDayKey } from './assetBookingCalendar';
import {
  defaultDateOnlyStartTime,
  isLocalCalendarDayToday,
} from './bookingScheduleDefaults';
import { openingTimesNormalizeDayValue } from './openingTimesLocale';
import { findOpeningTimesWeekdayKey, OPENING_TIMES_WEEKDAY_ORDER } from './openingTimesWeekdays';

function weekdayKeyFromCalendarDayKey(dayKey: string): string | null {
  const parsed = parseCalendarDayKey(dayKey);
  if (!parsed) {
    return null;
  }
  const date = new Date(parsed.year, parsed.monthIndex, parsed.day);
  const index = date.getDay();
  return OPENING_TIMES_WEEKDAY_ORDER[index] ?? null;
}

function parseHmToMinutes(hm: string): number | null {
  const match = /^(\d{2}):(\d{2})$/.exec(hm.trim());
  if (!match) {
    return null;
  }
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isFinite(hour) || !Number.isFinite(minute) || hour < 0 || hour > 23) {
    return null;
  }
  return hour * 60 + minute;
}

export type CalendarDayOpeningWindow = { openMin: number; closeMin: number } | 'closed' | null;

function windowFromNormalized(
  normalized: 'closed' | { open: string; close: string },
): Exclude<CalendarDayOpeningWindow, null> {
  if (normalized === 'closed') {
    return 'closed';
  }
  const openMin = parseHmToMinutes(normalized.open);
  const closeMin = parseHmToMinutes(normalized.close);
  if (openMin === null || closeMin === null || closeMin <= openMin) {
    return 'closed';
  }
  return { openMin, closeMin };
}

function dayOpeningWindow(
  openingTimes: Record<string, unknown>,
  dayKey: string,
): CalendarDayOpeningWindow {
  // Exact ISO date keys (blackouts / specific-date hours) override weekday schedule.
  // Only treat real calendar-day keys — never other opening_times properties.
  if (parseCalendarDayKey(dayKey) && Object.prototype.hasOwnProperty.call(openingTimes, dayKey)) {
    return windowFromNormalized(openingTimesNormalizeDayValue(openingTimes[dayKey]));
  }

  const weekday = weekdayKeyFromCalendarDayKey(dayKey);
  if (!weekday) {
    return null;
  }
  // Missing weekday keys must not mean "closed" for booking. Owner editor UI maps blanks to
  // closed, but assets with ISO blackouts often ship sparse weekday maps; treating those as
  // closed blocked every day. Fail open when the weekday is not explicitly set.
  const storedKey = findOpeningTimesWeekdayKey(openingTimes, weekday);
  if (!storedKey) {
    return null;
  }
  const raw = openingTimes[storedKey];
  if (raw === undefined || raw === null || raw === '') {
    return null;
  }
  return windowFromNormalized(openingTimesNormalizeDayValue(raw));
}

export function resolveCalendarDayOpeningWindow(
  asset: Record<string, unknown>,
  dayKey: string,
): CalendarDayOpeningWindow {
  const openingTimes = resolveAssetOpeningTimesForDisplay(asset);
  if (!openingTimes) {
    return null;
  }
  return dayOpeningWindow(openingTimes, dayKey);
}

export function calendarDayIsClosed(asset: Record<string, unknown>, dayKey: string): boolean {
  return resolveCalendarDayOpeningWindow(asset, dayKey) === 'closed';
}

/** True when `dayKey` is an explicit ISO date entry marked closed (blackout), not weekday closed. */
export function isIsoBlackoutDay(
  asset: Record<string, unknown> | null | undefined,
  dayKey: string,
): boolean {
  if (!asset || !parseCalendarDayKey(dayKey)) {
    return false;
  }
  const openingTimes = resolveAssetOpeningTimesForDisplay(asset);
  if (!openingTimes || !Object.prototype.hasOwnProperty.call(openingTimes, dayKey)) {
    return false;
  }
  return openingTimesNormalizeDayValue(openingTimes[dayKey]) === 'closed';
}

function minutesToHm(minutes: number): string {
  const hour = Math.floor(minutes / 60);
  return `${String(hour).padStart(2, '0')}:00`;
}

/** Whole-hour start times on `dayKey` that can begin at least a one-hour booking within opening hours. */
export function openingHourStartOptionsForDay(
  asset: Record<string, unknown>,
  dayKey: string,
): string[] {
  const window = resolveCalendarDayOpeningWindow(asset, dayKey);
  if (window === 'closed') {
    return [];
  }
  // No explicit weekday hours (e.g. blackout-only maps) → allow a full local day of slots.
  const openMin = window === null ? 0 : window.openMin;
  const closeMin = window === null ? 24 * 60 : window.closeMin;
  const out: string[] = [];
  for (let minute = openMin; minute + 60 <= closeMin; minute += 60) {
    out.push(minutesToHm(minute));
  }
  return out;
}

/** Whole-hour end times after `startHm` that still lie within opening hours on `dayKey`. */
export function openingHourEndOptionsForDay(
  asset: Record<string, unknown>,
  dayKey: string,
  startHm: string,
): string[] {
  const window = resolveCalendarDayOpeningWindow(asset, dayKey);
  const startMin = parseHmToMinutes(startHm);
  if (window === 'closed' || startMin === null || startMin % 60 !== 0) {
    return [];
  }
  const closeMin = window === null ? 24 * 60 : window.closeMin;
  const out: string[] = [];
  for (let minute = startMin + 60; minute <= closeMin; minute += 60) {
    if (minute >= 24 * 60) {
      break;
    }
    out.push(minutesToHm(minute));
  }
  return out;
}

/** True when every whole hour from `startHm` up to `endHm` lies within opening hours on `dayKey`. */
export function isHourlyRangeWithinOpeningHours(
  asset: Record<string, unknown>,
  dayKey: string,
  startHm: string,
  endHm: string,
): boolean {
  const openingTimes = resolveAssetOpeningTimesForDisplay(asset);
  if (!openingTimes) {
    return true;
  }
  const window = dayOpeningWindow(openingTimes, dayKey);
  if (window === null) {
    return true;
  }
  if (window === 'closed') {
    return false;
  }
  const startMin = parseHmToMinutes(startHm);
  const endMin = parseHmToMinutes(endHm);
  if (startMin === null || endMin === null || endMin <= startMin) {
    return false;
  }
  if (startMin % 60 !== 0 || endMin % 60 !== 0) {
    return false;
  }
  return startMin >= window.openMin && endMin <= window.closeMin;
}

/** True when each calendar day in the inclusive range is open (not marked closed). */
export function isDailyRangeWithinOpeningHours(
  asset: Record<string, unknown>,
  startDayKey: string,
  endDayKey: string,
): boolean {
  const openingTimes = resolveAssetOpeningTimesForDisplay(asset);
  if (!openingTimes) {
    return true;
  }
  for (const dayKey of eachCalendarDayInRange(startDayKey, endDayKey)) {
    const window = dayOpeningWindow(openingTimes, dayKey);
    if (window === 'closed') {
      return false;
    }
  }
  return true;
}

/**
 * True when an explicit ISO blackout / closed override exists under opening_times
 * for any day in the inclusive booking range. Does not enforce weekday closed schedules.
 */
export function bookingRangeHitsIsoBlackout(
  asset: Record<string, unknown> | null | undefined,
  startIso: string,
  endIso: string,
): boolean {
  if (!asset) {
    return false;
  }
  const openingTimes = resolveAssetOpeningTimesForDisplay(asset);
  if (!openingTimes) {
    return false;
  }
  const startDay = isoToCalendarDayKey(startIso);
  const endDay = isoToCalendarDayKey(endIso);
  if (!startDay || !endDay) {
    return false;
  }
  for (const dayKey of eachCalendarDayInRange(startDay, endDay)) {
    if (!Object.prototype.hasOwnProperty.call(openingTimes, dayKey)) {
      continue;
    }
    if (openingTimesNormalizeDayValue(openingTimes[dayKey]) === 'closed') {
      return true;
    }
  }
  return false;
}

/** True when the booking interval stays within opening hours (and no blackout days). */
export function isBookingScheduleWithinOpeningHours(
  asset: Record<string, unknown> | null | undefined,
  startIso: string,
  endIso: string,
  options?: { hourly?: boolean },
): boolean {
  if (!asset) {
    return true;
  }
  const openingTimes = resolveAssetOpeningTimesForDisplay(asset);
  if (!openingTimes) {
    return true;
  }
  const startDay = isoToCalendarDayKey(startIso);
  const endDay = isoToCalendarDayKey(endIso);
  if (!startDay || !endDay) {
    return true;
  }
  if (options?.hourly) {
    const startDt = new Date(startIso);
    const endDt = new Date(endIso);
    if (Number.isNaN(startDt.getTime()) || Number.isNaN(endDt.getTime())) {
      return false;
    }
    const startHm = `${String(startDt.getHours()).padStart(2, '0')}:${String(startDt.getMinutes()).padStart(2, '0')}`;
    const endHm = `${String(endDt.getHours()).padStart(2, '0')}:${String(endDt.getMinutes()).padStart(2, '0')}`;
    return isHourlyRangeWithinOpeningHours(asset, startDay, startHm, endHm);
  }
  return isDailyRangeWithinOpeningHours(asset, startDay, endDay);
}

export function countWholeHoursBetween(startHm: string, endHm: string): number {
  const startMin = parseHmToMinutes(startHm);
  const endMin = parseHmToMinutes(endHm);
  if (startMin === null || endMin === null || endMin <= startMin) {
    return 0;
  }
  if (startMin % 60 !== 0 || endMin % 60 !== 0) {
    return 0;
  }
  return (endMin - startMin) / 60;
}

export function buildLocalStartEndIso(
  dayKey: string,
  startHm: string,
  endHm: string,
): { startIso: string; endIso: string } | null {
  const parsed = parseCalendarDayKey(dayKey);
  const startMin = parseHmToMinutes(startHm);
  const endMin = parseHmToMinutes(endHm);
  if (!parsed || startMin === null || endMin === null || endMin <= startMin) {
    return null;
  }
  const start = new Date(parsed.year, parsed.monthIndex, parsed.day, 0, 0, 0, 0);
  start.setMinutes(startMin);
  const end = new Date(parsed.year, parsed.monthIndex, parsed.day, 0, 0, 0, 0);
  end.setMinutes(endMin);
  return { startIso: start.toISOString(), endIso: end.toISOString() };
}

export function buildDailyStartEndIso(
  startDayKey: string,
  endDayKey: string,
  now: Date = new Date(),
): { startIso: string; endIso: string } | null {
  const startParsed = parseCalendarDayKey(startDayKey);
  const endParsed = parseCalendarDayKey(endDayKey);
  if (!startParsed || !endParsed) {
    return null;
  }
  let start: Date;
  if (isLocalCalendarDayToday(startParsed.year, startParsed.monthIndex, startParsed.day, now)) {
    const hm = defaultDateOnlyStartTime(
      startParsed.year,
      startParsed.monthIndex,
      startParsed.day,
      now,
    );
    const startMin = parseHmToMinutes(hm);
    start = new Date(startParsed.year, startParsed.monthIndex, startParsed.day, 0, 0, 0, 0);
    if (startMin !== null) {
      start.setMinutes(startMin);
    }
  } else {
    start = new Date(startParsed.year, startParsed.monthIndex, startParsed.day, 0, 0, 0, 0);
  }
  const end = new Date(endParsed.year, endParsed.monthIndex, endParsed.day, 23, 59, 59, 999);
  if (end.getTime() < start.getTime()) {
    return null;
  }
  return { startIso: start.toISOString(), endIso: end.toISOString() };
}
